// Precyzyjny kalkulator magnesów neodymowych z dokładnymi wzorami fizycznymi
// Implementacja według specyfikacji technicznej z rzeczywistymi danymi

// ==================== STAŁE FIZYCZNE ====================
const MU_0 = 4 * Math.PI * 1e-7; // Przenikalność magnetyczna próżni [H/m]

// ==================== TYPY DANYCH ====================
export interface PrecisionMagnetInput {
  shape: 'disc' | 'block';
  // Wymiary w mm
  D?: number;           // średnica (dla dysku)
  L: number;            // grubość/wysokość (oś magnesowania)
  a?: number;           // szerokość (dla bloku)
  b?: number;           // długość (dla bloku)
  grade: string;        // np. "N42", "N42M", "N42H", etc.
  T: number;            // temperatura pracy [°C]
  airGap: number;       // szczelina powietrzna [mm]
  steelThickness: number; // grubość przyciąganej blachy [mm]
  orientation: 'normal' | 'shear'; // prostopadle lub po ścinaniu
  surface: 'milled' | 'painted' | 'rusty'; // jakość powierzchni
  safetyFactor: number; // współczynnik bezpieczeństwa (1.5-3)
}

export interface NeoGradeData {
  Br20_T: number;       // Remanencja w 20°C [T]
  Hcj20_kApm: number;   // Koercja w 20°C [kA/m]
  BHmax_kJpm3: [number, number]; // Zakres max energii [kJ/m³]
  Tmax_C: number;       // Max temperatura pracy [°C]
  alpha_Br: number;     // Współczynnik temp. dla Br [1/°C]
  beta_Hci: number;     // Współczynnik temp. dla Hci [1/°C]
}

// ==================== BAZA DANYCH MAGNESÓW ====================
export const NEO_GRADES: Record<string, NeoGradeData> = {
  // Standardowe gatunki (max 80°C)
  'N35': {
    Br20_T: 1.18,
    Hcj20_kApm: 955,
    BHmax_kJpm3: [263, 287],
    Tmax_C: 80,
    alpha_Br: -0.0012,
    beta_Hci: -0.006
  },
  'N38': {
    Br20_T: 1.23,
    Hcj20_kApm: 955,
    BHmax_kJpm3: [287, 310],
    Tmax_C: 80,
    alpha_Br: -0.0012,
    beta_Hci: -0.006
  },
  'N40': {
    Br20_T: 1.26,
    Hcj20_kApm: 955,
    BHmax_kJpm3: [302, 326],
    Tmax_C: 80,
    alpha_Br: -0.0012,
    beta_Hci: -0.006
  },
  'N42': {
    Br20_T: 1.30,
    Hcj20_kApm: 955,
    BHmax_kJpm3: [320, 350],
    Tmax_C: 80,
    alpha_Br: -0.0012,
    beta_Hci: -0.006
  },
  'N45': {
    Br20_T: 1.34,
    Hcj20_kApm: 955,
    BHmax_kJpm3: [342, 366],
    Tmax_C: 80,
    alpha_Br: -0.0012,
    beta_Hci: -0.006
  },
  'N48': {
    Br20_T: 1.38,
    Hcj20_kApm: 955,
    BHmax_kJpm3: [366, 390],
    Tmax_C: 80,
    alpha_Br: -0.0012,
    beta_Hci: -0.006
  },
  'N50': {
    Br20_T: 1.41,
    Hcj20_kApm: 876,
    BHmax_kJpm3: [382, 406],
    Tmax_C: 80,
    alpha_Br: -0.0012,
    beta_Hci: -0.006
  },
  'N52': {
    Br20_T: 1.44,
    Hcj20_kApm: 876,
    BHmax_kJpm3: [398, 422],
    Tmax_C: 80,
    alpha_Br: -0.0012,
    beta_Hci: -0.006
  },
  
  // Gatunki M (max 100°C)
  'N35M': {
    Br20_T: 1.18,
    Hcj20_kApm: 1114,
    BHmax_kJpm3: [263, 287],
    Tmax_C: 100,
    alpha_Br: -0.0012,
    beta_Hci: -0.0055
  },
  'N38M': {
    Br20_T: 1.23,
    Hcj20_kApm: 1114,
    BHmax_kJpm3: [287, 310],
    Tmax_C: 100,
    alpha_Br: -0.0012,
    beta_Hci: -0.0055
  },
  'N40M': {
    Br20_T: 1.26,
    Hcj20_kApm: 1114,
    BHmax_kJpm3: [302, 326],
    Tmax_C: 100,
    alpha_Br: -0.0012,
    beta_Hci: -0.0055
  },
  'N42M': {
    Br20_T: 1.30,
    Hcj20_kApm: 1114,
    BHmax_kJpm3: [320, 350],
    Tmax_C: 100,
    alpha_Br: -0.0012,
    beta_Hci: -0.0055
  },
  'N45M': {
    Br20_T: 1.34,
    Hcj20_kApm: 1114,
    BHmax_kJpm3: [342, 366],
    Tmax_C: 100,
    alpha_Br: -0.0012,
    beta_Hci: -0.0055
  },
  'N48M': {
    Br20_T: 1.38,
    Hcj20_kApm: 1114,
    BHmax_kJpm3: [366, 390],
    Tmax_C: 100,
    alpha_Br: -0.0012,
    beta_Hci: -0.0055
  },
  'N50M': {
    Br20_T: 1.41,
    Hcj20_kApm: 1033,
    BHmax_kJpm3: [382, 406],
    Tmax_C: 100,
    alpha_Br: -0.0012,
    beta_Hci: -0.0055
  },
  
  // Gatunki H (max 120°C)
  'N35H': {
    Br20_T: 1.18,
    Hcj20_kApm: 1353,
    BHmax_kJpm3: [263, 287],
    Tmax_C: 120,
    alpha_Br: -0.0011,
    beta_Hci: -0.005
  },
  'N38H': {
    Br20_T: 1.23,
    Hcj20_kApm: 1353,
    BHmax_kJpm3: [287, 310],
    Tmax_C: 120,
    alpha_Br: -0.0011,
    beta_Hci: -0.005
  },
  'N40H': {
    Br20_T: 1.26,
    Hcj20_kApm: 1353,
    BHmax_kJpm3: [302, 326],
    Tmax_C: 120,
    alpha_Br: -0.0011,
    beta_Hci: -0.005
  },
  'N42H': {
    Br20_T: 1.30,
    Hcj20_kApm: 1353,
    BHmax_kJpm3: [320, 350],
    Tmax_C: 120,
    alpha_Br: -0.0011,
    beta_Hci: -0.005
  },
  'N45H': {
    Br20_T: 1.34,
    Hcj20_kApm: 1353,
    BHmax_kJpm3: [342, 366],
    Tmax_C: 120,
    alpha_Br: -0.0011,
    beta_Hci: -0.005
  },
  'N48H': {
    Br20_T: 1.38,
    Hcj20_kApm: 1273,
    BHmax_kJpm3: [366, 390],
    Tmax_C: 120,
    alpha_Br: -0.0011,
    beta_Hci: -0.005
  },
  
  // Gatunki SH (max 150°C)
  'N35SH': {
    Br20_T: 1.18,
    Hcj20_kApm: 1592,
    BHmax_kJpm3: [263, 287],
    Tmax_C: 150,
    alpha_Br: -0.0011,
    beta_Hci: -0.0045
  },
  'N38SH': {
    Br20_T: 1.23,
    Hcj20_kApm: 1592,
    BHmax_kJpm3: [287, 310],
    Tmax_C: 150,
    alpha_Br: -0.0011,
    beta_Hci: -0.0045
  },
  'N40SH': {
    Br20_T: 1.26,
    Hcj20_kApm: 1592,
    BHmax_kJpm3: [302, 326],
    Tmax_C: 150,
    alpha_Br: -0.0011,
    beta_Hci: -0.0045
  },
  'N42SH': {
    Br20_T: 1.30,
    Hcj20_kApm: 1592,
    BHmax_kJpm3: [320, 350],
    Tmax_C: 150,
    alpha_Br: -0.0011,
    beta_Hci: -0.0045
  },
  'N45SH': {
    Br20_T: 1.34,
    Hcj20_kApm: 1512,
    BHmax_kJpm3: [342, 366],
    Tmax_C: 150,
    alpha_Br: -0.0011,
    beta_Hci: -0.0045
  },
  
  // Gatunki UH (max 180°C)
  'N35UH': {
    Br20_T: 1.18,
    Hcj20_kApm: 1990,
    BHmax_kJpm3: [263, 287],
    Tmax_C: 180,
    alpha_Br: -0.0011,
    beta_Hci: -0.004
  },
  'N38UH': {
    Br20_T: 1.23,
    Hcj20_kApm: 1990,
    BHmax_kJpm3: [287, 310],
    Tmax_C: 180,
    alpha_Br: -0.0011,
    beta_Hci: -0.004
  },
  'N40UH': {
    Br20_T: 1.26,
    Hcj20_kApm: 1990,
    BHmax_kJpm3: [302, 326],
    Tmax_C: 180,
    alpha_Br: -0.0011,
    beta_Hci: -0.004
  },
  'N42UH': {
    Br20_T: 1.30,
    Hcj20_kApm: 1990,
    BHmax_kJpm3: [320, 350],
    Tmax_C: 180,
    alpha_Br: -0.0011,
    beta_Hci: -0.004
  }
};

// ==================== FUNKCJE OBLICZENIOWE ====================

/**
 * Korekta temperaturowa dla remanencji magnetycznej
 */
function calculateBr(grade: NeoGradeData, T: number): number {
  return grade.Br20_T * (1 + grade.alpha_Br * (T - 20));
}

/**
 * Korekta temperaturowa dla koercji
 */
function calculateHci(grade: NeoGradeData, T: number): number {
  return grade.Hcj20_kApm * (1 + grade.beta_Hci * (T - 20));
}

/**
 * Obliczenie współczynnika geometrii dla magnesu
 */
function calculateGeometryFactor(shape: 'disc' | 'block', L: number, D?: number, a?: number, b?: number): number {
  let R_eq: number;
  
  if (shape === 'disc' && D) {
    R_eq = D / 2;
  } else if (shape === 'block' && a && b) {
    // Promień równoważny dla prostokąta
    R_eq = 0.5 * Math.sqrt(a * b / Math.PI);
  } else {
    throw new Error('Nieprawidłowe wymiary dla wybranego kształtu');
  }
  
  // Współczynnik geometrii z uwzględnieniem smukłości
  const ratio = L / R_eq;
  const k_geo = Math.max(0.60, Math.min(0.80, 0.55 + 0.25 * Math.tanh(1.2 * ratio)));
  
  return k_geo;
}

/**
 * Obliczenie pola powierzchni styku
 */
function calculateContactArea(shape: 'disc' | 'block', D?: number, a?: number, b?: number): number {
  if (shape === 'disc' && D) {
    return Math.PI * Math.pow(D / 2, 2); // mm²
  } else if (shape === 'block' && a && b) {
    return a * b; // mm²
  } else {
    throw new Error('Nieprawidłowe wymiary dla wybranego kształtu');
  }
}

/**
 * Współczynnik korekcji dla szczeliny powietrznej
 */
function calculateAirGapFactor(airGap: number, L: number, R_eq: number): number {
  const lambda = Math.min(L, R_eq);
  return Math.pow(1 / (1 + airGap / lambda), 2);
}

/**
 * Współczynnik korekcji dla grubości stali
 */
function calculateSteelThicknessFactor(steelThickness: number, R_eq: number): number {
  const t_sat = 0.5 * R_eq; // Empiryczna grubość nasycenia
  return 1 - Math.exp(-steelThickness / t_sat);
}

/**
 * Współczynnik korekcji dla jakości powierzchni
 */
function getSurfaceFactor(surface: 'milled' | 'painted' | 'rusty'): number {
  const factors = {
    'milled': 1.00,   // Frezowana, czysta powierzchnia
    'painted': 0.85,  // Malowana powierzchnia
    'rusty': 0.70     // Zardzewiała/nierówna powierzchnia
  };
  return factors[surface];
}

/**
 * Główna funkcja obliczająca siłę trzymania magnesu
 */
export function calculatePrecisionPullForce(input: PrecisionMagnetInput): {
  F_normal: number;      // Siła normalna [N]
  F_shear: number;       // Siła ścinania [N]
  F_design: number;      // Siła projektowa (z współczynnikiem bezpieczeństwa) [N]
  B_gap: number;         // Indukcja w szczelinie [T]
  warnings: string[];
  details: {
    Br_T: number;
    k_geo: number;
    f_gap: number;
    f_steel: number;
    f_surface: number;
    A_mm2: number;
    R_eq: number;
  };
} {
  const warnings: string[] = [];
  
  // Pobierz dane gatunku
  const gradeData = NEO_GRADES[input.grade];
  if (!gradeData) {
    throw new Error(`Nieznany gatunek magnesu: ${input.grade}`);
  }
  
  // Walidacja temperatury
  if (input.T > gradeData.Tmax_C) {
    warnings.push(`⚠️ UWAGA: Temperatura ${input.T}°C przekracza limit ${gradeData.Tmax_C}°C dla gatunku ${input.grade}. Ryzyko nieodwracalnej demagnetyzacji!`);
  }
  
  if (input.T < -40) {
    warnings.push('⚠️ Bardzo niska temperatura może zwiększyć kruchość magnesów');
  }
  
  // Obliczenia podstawowe
  const A = calculateContactArea(input.shape, input.D, input.a, input.b);
  const A_m2 = A * 1e-6; // Konwersja mm² na m²
  
  // Promień równoważny
  let R_eq: number;
  if (input.shape === 'disc' && input.D) {
    R_eq = input.D / 2;
  } else if (input.shape === 'block' && input.a && input.b) {
    R_eq = 0.5 * Math.sqrt(input.a * input.b / Math.PI);
  } else {
    throw new Error('Nieprawidłowe wymiary');
  }
  
  // Korekta temperaturowa remanencji
  const Br_T = calculateBr(gradeData, input.T);
  
  // Współczynnik geometrii
  const k_geo = calculateGeometryFactor(input.shape, input.L, input.D, input.a, input.b);
  
  // Indukcja w szczelinie
  const B_gap = k_geo * Br_T;
  
  // Siła podstawowa (bez korekcji)
  const F0 = (Math.pow(B_gap, 2) * A_m2) / (2 * MU_0);
  
  // Współczynniki korekcyjne
  const f_gap = calculateAirGapFactor(input.airGap, input.L, R_eq);
  const f_steel = calculateSteelThicknessFactor(input.steelThickness, R_eq);
  const f_surface = getSurfaceFactor(input.surface);
  
  // Ostrzeżenia dotyczące warunków
  if (input.steelThickness < 0.2 * R_eq) {
    warnings.push(`⚠️ Blacha zbyt cienka (${input.steelThickness}mm). Rozważ grubszą podkładkę stalową. Redukcja siły: ${((1 - f_steel) * 100).toFixed(0)}%`);
  }
  
  if (input.airGap > 0.5 * R_eq) {
    warnings.push(`⚠️ Duża szczelina (${input.airGap}mm). Siła spadnie do ${(f_gap * 100).toFixed(0)}% wartości nominalnej`);
  }
  
  if (input.surface === 'rusty') {
    warnings.push('⚠️ Zardzewiała powierzchnia redukuje siłę o ~30%');
  } else if (input.surface === 'painted') {
    warnings.push('ℹ️ Malowana powierzchnia redukuje siłę o ~15%');
  }
  
  // Obliczenie siły końcowej
  const F_normal = F0 * f_gap * f_steel * f_surface;
  
  // Siła ścinania (tarcie)
  const mu_effective = 0.5; // Współczynnik tarcia dla stali
  const F_shear = mu_effective * F_normal;
  
  if (input.orientation === 'shear') {
    warnings.push('ℹ️ W trybie ścinania polegasz na tarciu. Rozważ mechaniczne zabezpieczenie (kołek, obrzeże)');
  }
  
  // Siła projektowa (z współczynnikiem bezpieczeństwa)
  const F_design = (input.orientation === 'shear' ? F_shear : F_normal) / input.safetyFactor;
  
  return {
    F_normal,
    F_shear,
    F_design,
    B_gap,
    warnings,
    details: {
      Br_T,
      k_geo,
      f_gap,
      f_steel,
      f_surface,
      A_mm2: A,
      R_eq
    }
  };
}

/**
 * Funkcja pomocnicza do obliczenia liczby magnesów potrzebnych dla danego obciążenia
 */
export function calculateRequiredMagnets(
  loadWeight_kg: number,
  magnetForce_N: number,
  safetyFactor: number = 2,
  orientation: 'vertical' | 'horizontal' | 'angled' = 'vertical',
  angle_deg: number = 0
): {
  quantity: number;
  totalForce_N: number;
  loadForce_N: number;
  warnings: string[];
} {
  const g = 9.81; // m/s²
  let loadForce_N = loadWeight_kg * g;
  const warnings: string[] = [];
  
  // Korekta dla orientacji
  if (orientation === 'horizontal') {
    loadForce_N *= 1.5; // Dodatkowy współczynnik dla montażu poziomego
    warnings.push('ℹ️ Montaż poziomy wymaga 50% więcej siły');
  } else if (orientation === 'angled') {
    const angle_rad = (angle_deg * Math.PI) / 180;
    loadForce_N *= (1 + Math.sin(angle_rad));
    warnings.push(`ℹ️ Montaż pod kątem ${angle_deg}° wymaga dodatkowej siły`);
  }
  
  // Zastosuj współczynnik bezpieczeństwa
  const requiredForce_N = loadForce_N * safetyFactor;
  
  // Oblicz liczbę magnesów
  const quantity = Math.ceil(requiredForce_N / magnetForce_N);
  const totalForce_N = quantity * magnetForce_N;
  
  if (quantity > 20) {
    warnings.push('⚠️ Duża liczba magnesów - rozważ użycie silniejszego gatunku lub większych magnesów');
  }
  
  return {
    quantity,
    totalForce_N,
    loadForce_N,
    warnings
  };
}

/**
 * Funkcja do porównania różnych gatunków magnesów
 */
export function compareGrades(
  input: Omit<PrecisionMagnetInput, 'grade'>,
  grades: string[]
): Array<{
  grade: string;
  force_N: number;
  force_kg: number;
  tempMargin_C: number;
  cost_relative: number;
  efficiency: number;
}> {
  const results = grades.map(grade => {
    const gradeData = NEO_GRADES[grade];
    if (!gradeData) return null;
    
    const result = calculatePrecisionPullForce({
      ...input,
      grade
    });
    
    // Względny koszt (N52 = 2.0, N35 = 1.0)
    const gradeNumber = parseInt(grade.match(/\d+/)?.[0] || '35');
    const cost_relative = 0.8 + (gradeNumber - 35) * 0.03;
    
    // Efektywność (siła/koszt)
    const efficiency = result.F_design / cost_relative;
    
    return {
      grade,
      force_N: result.F_design,
      force_kg: result.F_design / 9.81,
      tempMargin_C: gradeData.Tmax_C - input.T,
      cost_relative,
      efficiency
    };
  }).filter(Boolean) as any[];
  
  // Sortuj według efektywności
  return results.sort((a, b) => b.efficiency - a.efficiency);
}

/**
 * Rekomendacja optymalnego gatunku magnesu
 */
export function recommendOptimalGrade(
  input: Omit<PrecisionMagnetInput, 'grade'>,
  constraints: {
    minForce_N?: number;
    maxCost?: number;
    preferHighTemp?: boolean;
  } = {}
): {
  recommended: string;
  reason: string;
  alternatives: string[];
} {
  const testGrades = Object.keys(NEO_GRADES);
  const validGrades: Array<{grade: string; score: number; reasons: string[]}> = [];
  
  for (const grade of testGrades) {
    const gradeData = NEO_GRADES[grade];
    let score = 100;
    const reasons: string[] = [];
    
    // Sprawdź temperaturę
    if (input.T > gradeData.Tmax_C - 20) {
      score -= 50;
      reasons.push('Blisko limitu temperatury');
    } else if (input.T <= gradeData.Tmax_C - 40) {
      score += 10;
      reasons.push('Duży zapas temperatury');
    }
    
    // Preferuj popularne gatunki
    if (['N35', 'N42', 'N45', 'N52'].includes(grade.replace(/[MH]|SH|UH/, ''))) {
      score += 20;
      reasons.push('Popularny, łatwo dostępny');
    }
    
    // Wysokotemperaturowe jeśli wymagane
    if (constraints.preferHighTemp && grade.includes('SH')) {
      score += 30;
      reasons.push('Wysoka odporność temperaturowa');
    }
    
    // Stosunek jakości do ceny
    const gradeNumber = parseInt(grade.match(/\d+/)?.[0] || '35');
    if (gradeNumber === 42) {
      score += 15;
      reasons.push('Najlepszy stosunek siła/cena');
    }
    
    if (score > 50) {
      validGrades.push({ grade, score, reasons });
    }
  }
  
  // Sortuj według wyniku
  validGrades.sort((a, b) => b.score - a.score);
  
  if (validGrades.length === 0) {
    return {
      recommended: 'N42',
      reason: 'Domyślny wybór - uniwersalny gatunek',
      alternatives: ['N35', 'N45']
    };
  }
  
  return {
    recommended: validGrades[0].grade,
    reason: validGrades[0].reasons.join(', '),
    alternatives: validGrades.slice(1, 4).map(v => v.grade)
  };
}

/**
 * Eksport danych do raportu technicznego
 */
export function generateTechnicalReport(
  input: PrecisionMagnetInput,
  result: ReturnType<typeof calculatePrecisionPullForce>
): string {
  const gradeData = NEO_GRADES[input.grade];
  
  return `
RAPORT TECHNICZNY - OBLICZENIA MAGNETYCZNE
==========================================

1. DANE WEJŚCIOWE
-----------------
Gatunek magnesu: ${input.grade}
Kształt: ${input.shape === 'disc' ? 'Dysk' : 'Blok'}
Wymiary: ${input.shape === 'disc' ? `Ø${input.D}×${input.L}mm` : `${input.a}×${input.b}×${input.L}mm`}
Temperatura pracy: ${input.T}°C
Szczelina powietrzna: ${input.airGap}mm
Grubość stali: ${input.steelThickness}mm
Orientacja: ${input.orientation === 'normal' ? 'Prostopadła' : 'Ścinanie'}
Powierzchnia: ${input.surface === 'milled' ? 'Frezowana' : input.surface === 'painted' ? 'Malowana' : 'Zardzewiała'}
Współczynnik bezpieczeństwa: ${input.safetyFactor}

2. PARAMETRY MAGNETYCZNE
------------------------
Remanencja Br (20°C): ${gradeData.Br20_T} T
Remanencja Br (${input.T}°C): ${result.details.Br_T.toFixed(3)} T
Koercja Hcj (20°C): ${gradeData.Hcj20_kApm} kA/m
Max energia (BH)max: ${gradeData.BHmax_kJpm3[0]}-${gradeData.BHmax_kJpm3[1]} kJ/m³
Max temperatura: ${gradeData.Tmax_C}°C
Współczynnik temp. αBr: ${gradeData.alpha_Br * 100}%/°C

3. OBLICZENIA SZCZEGÓŁOWE
-------------------------
Powierzchnia styku: ${result.details.A_mm2.toFixed(1)} mm² (${(result.details.A_mm2 * 1e-6).toFixed(6)} m²)
Promień równoważny: ${result.details.R_eq.toFixed(1)} mm
Współczynnik geometrii k_geo: ${result.details.k_geo.toFixed(3)}
Indukcja w szczelinie B_gap: ${result.B_gap.toFixed(3)} T

Współczynniki korekcyjne:
- Szczelina powietrzna f_gap: ${result.details.f_gap.toFixed(3)}
- Grubość stali f_steel: ${result.details.f_steel.toFixed(3)}
- Jakość powierzchni f_surface: ${result.details.f_surface.toFixed(3)}

4. WYNIKI
---------
Siła normalna (prostopadła): ${result.F_normal.toFixed(1)} N (${(result.F_normal / 9.81).toFixed(2)} kg)
Siła ścinania (równoległa): ${result.F_shear.toFixed(1)} N (${(result.F_shear / 9.81).toFixed(2)} kg)
Siła projektowa: ${result.F_design.toFixed(1)} N (${(result.F_design / 9.81).toFixed(2)} kg)

5. OSTRZEŻENIA I ZALECENIA
--------------------------
${result.warnings.length > 0 ? result.warnings.join('\n') : 'Brak ostrzeżeń'}

6. NORMY I ŹRÓDŁA
-----------------
- Obliczenia zgodne z teorią pola magnetycznego (Maxwell)
- Dane materiałowe wg kart katalogowych producentów (Arnold Magnetic, K&J Magnetics)
- Współczynniki temperaturowe wg IEC 60404-8-1
- Współczynniki bezpieczeństwa wg DIN EN 13155

Data wygenerowania: ${new Date().toLocaleString('pl-PL')}
`;
}
